import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, sendPhoneCode, verifyPhoneCode } from "./loginApi";
import {
  selectAccId,
  selectFromClient,
  selectIsLogOut,
  selectUsers,
  setAccId,
  setCurrentUser,
  setFromClient,
  setName,
  setUserId,
  setUsers,
} from "./loginSlice";
import { useNavigate } from "react-router-dom";
import "./login.css";
import { rBlueIcon, rIcon } from "../../static/svgs";
import { useSocket } from "../chat/useSocket";
import { connectionFailedMessage } from "../../utils/fetchWithRetry";

const Login = () => {
  const { socket } = useSocket();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const users = useSelector(selectUsers);
  const isLogout = useSelector(selectIsLogOut);
  const from_client = useSelector(selectFromClient);
  
  // Состояния для регистрации
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationPhone, setRegistrationPhone] = useState("");
  const [registrationCode, setRegistrationCode] = useState("");
  const [registrationName, setRegistrationName] = useState("");
  const [registrationPassword, setRegistrationPassword] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("");
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);

  // Функция для нормализации номера телефона
  const normalizePhone = (value) => {
    // Удаляем все нецифровые символы
    let digits = value.replace(/\D/g, '');
    
    // Если номер начинается с 8, заменяем на 7
    if (digits.length > 0 && digits[0] === '8') {
      digits = '7' + digits.substring(1);
    }
    
    // Ограничиваем длину до 11 символов (формат 79999999999)
    if (digits.length > 11) {
      digits = digits.substring(0, 11);
    }
    
    return digits;
  };

  const handleLogin = () => {
    setIsLoading(true);
    setConnectionStatus("");
    loginUser(username, password, {
      onRetryProgress: ({ message, status }) => {
        if (message && status !== "completed") {
          setConnectionStatus(message);
        }
      },
    })
      .then((data) => {
        setConnectionStatus("");
        if (data.error) {
          alert("Неверные данные");
        } else {
          console.log("handleLogin", data);
          dispatch(setName(data.name));
          dispatch(setUserId(data.id));
          dispatch(setAccId(data.accountant_id));
          dispatch(setCurrentUser(data));
          // onLogin(data);
          const newUser = {
            username,
            password,
            company_name: data.name,
            user_id: data.id,
          };
          const existingUserIndex = users.findIndex(
            (user) => user.username === newUser.username
          );
          if (existingUserIndex === -1) {
            const updatedUsers = [...users, newUser];
            localStorage.setItem("userList", JSON.stringify(updatedUsers));
            dispatch(setUsers(updatedUsers));
          } else {
            const updatedUsers = [...users];
            updatedUsers[existingUserIndex] = newUser;
            localStorage.setItem("userList", JSON.stringify(updatedUsers));
            dispatch(setUsers(updatedUsers));
          }
          navigate("/client");
        }
      })
      .catch((error) => {
        console.error("Ошибка при входе:", error);
        setConnectionStatus(error.userMessage || connectionFailedMessage());
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleBackClient = () => {
    dispatch(setFromClient(false));
    navigate("/client");
  };

  const handleSendCode = async () => {
    if (!registrationPhone) {
      alert("Введите номер телефона");
      return;
    }
    if (!agreementAccepted) {
      alert("Необходимо согласиться с пользовательским соглашением");
      return;
    }
    setIsLoading(true);
    try {
      const result = await sendPhoneCode(registrationPhone);
      if (result.success) {
        setCodeSent(true);
      } else {
        alert(result.error || "Ошибка при отправке кода");
      }
    } catch (error) {
      console.error("Ошибка при отправке кода:", error);
      alert("Ошибка при отправке кода");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!registrationCode || !registrationName || !registrationPassword) {
      alert("Заполните все поля");
      return;
    }
    setIsLoading(true);
    try {
      const result = await verifyPhoneCode(
        registrationPhone,
        registrationCode,
        registrationName,
        registrationPassword
      );
      if (result.success && result.user) {
        // Автоматически логиним пользователя
        const userData = result.user;
        dispatch(setName(userData.name));
        dispatch(setUserId(userData.id));
        dispatch(setAccId(userData.accountant_id || null));
        dispatch(setCurrentUser(userData));
        
        const newUser = {
          username: registrationPhone,
          password: registrationPassword,
          company_name: userData.name,
          user_id: userData.id,
        };
        const updatedUsers = [...users, newUser];
        localStorage.setItem("userList", JSON.stringify(updatedUsers));
        dispatch(setUsers(updatedUsers));
        localStorage.setItem("user_id", userData.id);
        
        navigate("/client");
      } else {
        alert(result.error || "Неверный код");
      }
    } catch (error) {
      console.error("Ошибка при проверке кода:", error);
      alert("Ошибка при проверке кода");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistrationClick = () => {
    setShowRegistration(true);
    setCodeSent(false);
    setRegistrationPhone("");
    setRegistrationCode("");
    setRegistrationName("");
    setRegistrationPassword("");
    setAgreementAccepted(false);
  };

  const handleCancelRegistration = () => {
    setShowRegistration(false);
    setCodeSent(false);
    setRegistrationPhone("");
    setRegistrationCode("");
    setRegistrationName("");
    setRegistrationPassword("");
    setAgreementAccepted(false);
  };

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = localStorage.getItem("userList");
        if (userData) {
          const parsedUserData = JSON.parse(userData);
          dispatch(setUsers(parsedUserData));
          if (parsedUserData.length > 0) {
            const lastUser = parsedUserData[parsedUserData.length - 1];
            loginUser(lastUser.username, lastUser.password, {
              onRetryProgress: ({ message, status }) => {
                if (message && status !== "completed") {
                  console.log("[auto-login]", message);
                }
              },
            })
              .then((data) => {
                if (!data.error) {
                  const newUser = {
                    username: lastUser.username,
                    password: lastUser.password,
                    company_name: data.name,
                    user_id: data.id,
                  };
                  const updatedUsers = [...parsedUserData];
                  updatedUsers[parsedUserData.length - 1] = newUser;
                  localStorage.setItem(
                    "userList",
                    JSON.stringify(updatedUsers)
                  );

                  try {
                    const userId = localStorage.getItem("user_id");
                    console.log("userId", userId);
                    if (!userId) {
                        console.log('register from login')
                        socket.emit("register", {
                            user_id: userId,
                            is_buh: false
                        });
                    }
                  } catch (err) {
                    console.warn(
                      "Ошибка получения user_id из localStorage",
                      err
                    );
                  }

                  localStorage.setItem("user_id", data.id);
                  console.log("localstorage", data);
                  dispatch(setUsers(updatedUsers));
                  dispatch(setName(data.name));
                  dispatch(setUserId(data.id));
                  dispatch(setAccId(data.accountant_id));
                  dispatch(setCurrentUser(data));
                  // navigate('/home')
                  navigate("/client");
                  // onLogin(data);
                }
              })
              .catch((error) => {
                console.error("Ошибка при автоматической авторизации:", error);
              });
          }
        }
      } catch (error) {
        console.error("Failed to load user data:", error);
      }
    };

    if (!isLogout) loadUserData();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        // gap: '30px',
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        backgroundColor: "#3262FF",
      }}
    >
      <div className="login_menu_cntr"></div>

      <div className="top_login_container">
        <label className="manrope-bold color_white">Вход</label>
        <div className="ricon_cntr1">{rIcon}</div>
      </div>
      {/* <div className='title_cntr'>
                {rBlueIcon}
                <label style={{ marginLeft: '-8px' }} className='philosopher-bold-italic color_white color_blue'>егистратор аутсорсинг</label>
            </div> */}
      <div className="cntr_login">
        {!showRegistration ? (
          <>
            <label
              style={{ textAlign: "left", width: "100%" }}
              className="color_dark manrope-bold"
            >
              Логин
            </label>
            <input
              className="input_atrib manrope-bold"
              type="text"
              placeholder="Номер телефона (без +)"
              value={username}
              onChange={(e) => setUsername(normalizePhone(e.target.value))}
            />
            <label
              style={{ textAlign: "left", width: "100%" }}
              className="color_dark manrope-bold"
            >
              Пароль
            </label>
            <input
              className="input_atrib manrope-bold"
              type="password"
              placeholder="Пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              className="btn_login manrope-bold"
              onClick={handleLogin}
              disabled={isLoading}
            >
              {isLoading ? "Подключение…" : "Войти"}
            </button>
            {connectionStatus ? (
              <div
                style={{
                  marginTop: "10px",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  backgroundColor: "#fff3cd",
                  color: "#664d03",
                  fontSize: "13px",
                  lineHeight: 1.4,
                  textAlign: "center",
                }}
                className="manrope-bold"
              >
                {connectionStatus}
              </div>
            ) : null}
            <div
              style={{
                textAlign: "center",
                marginTop: "10px",
                cursor: "pointer",
                color: "#3262FF",
                fontSize: "14px",
              }}
              className="manrope-bold"
              onClick={handleRegistrationClick}
            >
              Регистрация
            </div>
          </>
        ) : (
          <>
            {!codeSent ? (
              <>
                <label
                  style={{ textAlign: "left", width: "100%" }}
                  className="color_dark manrope-bold"
                >
                  Номер телефона
                </label>
                <input
                  className="input_atrib manrope-bold"
                  type="tel"
                  placeholder="Номер телефона (без +)"
                  value={registrationPhone}
                  onChange={(e) => setRegistrationPhone(normalizePhone(e.target.value))}
                  disabled={isLoading}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "10px",
                  }}
                >
                  <input
                    type="checkbox"
                    id="agreement-checkbox"
                    checked={agreementAccepted}
                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                    }}
                  />
                  <label
                    htmlFor="agreement-checkbox"
                    style={{
                      fontSize: "14px",
                      color: "#282932",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    className="manrope-bold"
                  >
                    Согласен с{" "}
                    <span
                      onClick={(e) => {
                        e.preventDefault();
                        setShowAgreementModal(true);
                      }}
                      style={{
                        color: "#3262FF",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      пользовательским соглашением
                    </span>
                  </label>
                </div>
                <button
                  className="btn_login manrope-bold"
                  onClick={handleSendCode}
                  disabled={isLoading || !agreementAccepted}
                  style={{
                    backgroundColor: isLoading || !agreementAccepted ? "#94A3B8" : "#3262FF",
                    cursor: isLoading || !agreementAccepted ? "not-allowed" : "pointer",
                  }}
                >
                  {isLoading ? "Отправка..." : "Отправить"}
                </button>
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "10px",
                    cursor: "pointer",
                    color: "#94A3B8",
                    fontSize: "14px",
                  }}
                  className="manrope-bold"
                  onClick={handleCancelRegistration}
                >
                  Отмена
                </div>
              </>
            ) : (
              <>
                <label
                  style={{ textAlign: "left", width: "100%" }}
                  className="color_dark manrope-bold"
                >
                  Код подтверждения
                </label>
                <input
                  className="input_atrib manrope-bold"
                  type="text"
                  placeholder="Введите код"
                  value={registrationCode}
                  onChange={(e) => setRegistrationCode(e.target.value)}
                  disabled={isLoading}
                  maxLength={4}
                />
                <label
                  style={{ textAlign: "left", width: "100%" }}
                  className="color_dark manrope-bold"
                >
                  Название
                </label>
                <input
                  className="input_atrib manrope-bold"
                  type="text"
                  placeholder="Ваше имя"
                  value={registrationName}
                  onChange={(e) => setRegistrationName(e.target.value)}
                  disabled={isLoading}
                />
                <label
                  style={{ textAlign: "left", width: "100%" }}
                  className="color_dark manrope-bold"
                >
                  Пароль
                </label>
                <input
                  className="input_atrib manrope-bold"
                  type="password"
                  placeholder="Придумайте пароль"
                  value={registrationPassword}
                  onChange={(e) => setRegistrationPassword(e.target.value)}
                  disabled={isLoading}
                />
                <button
                  className="btn_login manrope-bold"
                  onClick={handleVerifyCode}
                  disabled={isLoading}
                  style={{
                    backgroundColor: isLoading ? "#94A3B8" : "#3262FF",
                    cursor: isLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {isLoading ? "Проверка..." : "ОК"}
                </button>
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "10px",
                    cursor: "pointer",
                    color: "#94A3B8",
                    fontSize: "14px",
                  }}
                  className="manrope-bold"
                  onClick={handleCancelRegistration}
                >
                  Отмена
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Модальное окно с пользовательским соглашением */}
      {showAgreementModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowAgreementModal(false)}
        >
          <div
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "600px",
              maxHeight: "80vh",
              width: "90%",
              overflow: "auto",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "20px",
                  color: "#282932",
                }}
                className="manrope-bold"
              >
                Пользовательское соглашение
              </h2>
              <button
                onClick={() => setShowAgreementModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#94A3B8",
                  padding: "0",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#282932",
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
              }}
              className="manrope-bold"
            >
              Настоящее согласие подтверждает, что в соответствии с требованиями ст. 9 Федерального закона РФ «О персональных данных», субъект персональных данных лично, свободно, в своём интересе и своей волей выражает согласие на автоматизированную и/или без использования средств автоматизации обработку персональных данных, указанных в настоящем согласии, а также на получение оператором персональных данных субъекта от третьих лиц.
Оператор обработки персональных данных: общество с ограниченной ответственностью «Регистратор», юридический адрес: 191124, город Санкт-Петербург, улица Новгородская, дом 23, литер А, помещение 141-Н №245; ОГРН 1167847076308; ИНН 7842088680; КПП 784201001 – далее – «оператор».
Персональные данные, подлежащие обработке: фамилия, имя, отчество, дата рождения, место рождения, пол, состояние в браке, паспортные данные, адрес места жительства и/или места временного проживания, дата регистрации по месту жительства, номера телефонов, иная контактная информация, идентификационный номер налогоплательщика, фотография, а также иные персональные данные, сообщённые субъектом персональных данных.
Цель обработки персональных данных: заключение, надлежащее исполнение, любые иные дополнительные действия сторон, в рамках заключенного субъектом и оператором гражданско-правового договора.
Действия с персональными данными, на которые даётся согласие: сбор, запись на любой вид носителей (бумажный, электронный и пр.), систематизация, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, передача (распространение, предоставление, доступ), обезличивание, блокирование, удаление, уничтожение персональных данных.
Лица, которым может быть поручена обработка персональных данных: банки, нотариусы, архивные организации, государственные и муниципальные службы РФ, осуществляющие любые регистрационные действия, предусмотренные действующим законодательством РФ, в рамках заключенного субъектом и оператором гражданско-правового договора.
Срок: настоящее согласие действует бессрочно. Настоящее согласие, в любой момент, может быть отозвано субъектом персональных данных в порядке, установленном Федеральным законом РФ «О персональных данных» .
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
